import { TimePicker, TimePickerProps, Typography } from 'antd';
import dayjs from 'dayjs';
import { get } from 'lodash-es';
import { Control, Controller, FieldValues, Path } from 'react-hook-form';

import { getErrorStatus } from '../../utils/form';

import FieldWithLabel from './FieldWithLabel';
import { FieldProps } from './types';

type TimeFieldProps<TFieldValues extends FieldValues> = TimePickerProps &
  FieldProps & {
    control: Control<TFieldValues>;
    name: Path<TFieldValues>;
    caption?: string;
  };

function TimeField<TFieldValues extends FieldValues>({
  name,
  label,
  required,
  control,
  hideError,
  caption,
  hideErrorMessage,
  ...props
}: TimeFieldProps<TFieldValues>) {
  return (
    <Controller
      name={name}
      control={control}
      render={({ field: { onChange, value, ...rest }, formState }) => {
        const error = get(formState.errors, name);
        const errorMessage =
          !hideError && typeof error?.message === 'string'
            ? error.message
            : undefined;

        const status = !hideError && errorMessage ? getErrorStatus(error) : '';

        return (
          <FieldWithLabel label={label} required={required}>
            <TimePicker
              className={'w-full'}
              status={status}
              onChange={(v) => {
                onChange(v?.format('HH:mm') || '');
              }}
              value={
                value
                  ? dayjs(`${dayjs().format('YYYY-MM-DD')} ${value as string}`)
                  : null
              }
              {...rest}
              {...props}
            />
            {caption && (
              <Typography.Paragraph type='secondary' className='mb-0'>
                {caption}
              </Typography.Paragraph>
            )}
            {!hideErrorMessage && errorMessage && (
              <p className={'text-xs text-red-500'}>{errorMessage}</p>
            )}
          </FieldWithLabel>
        );
      }}
    />
  );
}

export default TimeField;
